/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { DEFAULT_INPUT_EARLIEST_ALLOWED_DATE, DEFAULT_INPUT_LATEST_ALLOWED_DATE } from "abreact/input/date/AbDateInput";
import { AbMonthInput } from "abreact/input/date/AbMonthInput";
import { AbYearInput } from "abreact/input/date/AbYearInput";
import { AbButton } from "abreact/misc/AbButton";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbIterable } from "abweb/collection/AbIterable";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { AbCalendarDay } from "abweb/utils/date/AbCalendarDay";
import { CalendarUtils } from "abweb/utils/date/CalendarUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { DateUtils } from "abweb/utils/DateUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import * as React from "react";
const firstDateOfMonth = (year, month) => {
  return AbCalendarDay.fromData({
    year: year,
    month: month,
    day: 1
  });
};
const lastDateOfMonth = (year, month) => {
  return add({
    year: year,
    month: month,
    day: 1
  }, {
    month: 1,
    day: -1
  });
};
const add = (value, delta) => {
  const d = DateUtils.newLocalDateWithFullYear(value.year, value.month - 1, value.day);
  return AbCalendarDay.fromLocalDate(DateUtils.offsetDate(d, delta));
};
const clampDate = (dateRaw, earliest, latest) => {
  const date = AbCalendarDay.fromData(dateRaw);
  if (earliest && date.compare(earliest) === -1) return AbCalendarDay.fromData(earliest);
  if (latest && date.compare(latest) === 1) return AbCalendarDay.fromData(latest);
  return date;
};
const getInitialDay = (dayValue, earliest, latest) => {
  const valueDate = dayValue ?? AbCalendarDay.fromLocalDate(new Date());
  return clampDate(valueDate, earliest, latest);
};
const offsetDay = (day, offset) => {
  return AbCalendarDay.fromLocalDate(DateUtils.offsetDate(day.localMidnightDate, offset));
};
const dateRange = (start, end) => {
  if (start.compare(end) > 0) {
    throw new Error("End of date range must come after beginning.");
  }
  return AbIterable.from({
    [Symbol.iterator]: function* () {
      let currentDate = AbCalendarDay.fromData(start);
      while (currentDate.compare(end) <= 0) {
        yield currentDate;
        currentDate = add(currentDate, {
          day: 1
        });
      }
    }
  });
};
const previousMonthFirstVisibleDate = (year, month) => {
  const firstDayNum = CalendarUtils.firstDay;
  const days = firstDateOfMonth(year, month).localMidnightDate.getDay() - firstDayNum + (firstDateOfMonth(year, month).localMidnightDate.getDay() < firstDayNum ? 7 : 0);
  return add(firstDateOfMonth(year, month), {
    day: -1 * days
  });
};
const nextMonthLastVisibleDate = (year, month) => {
  const days = (6 + CalendarUtils.firstDay - lastDateOfMonth(year, month).localMidnightDate.getDay()) % 7;
  return add(firstDateOfMonth(year, month + 1), {
    day: days - 1
  });
};
export const AbCalendar = props => {
  const [focusDay, setFocusDay] = React.useState(getInitialDay(props.value, props.earliestDay, props.latestDay));
  const partialRef = React.useRef({}).current;
  const isRefReady = partial => !!partial.calendarElement && !!partial.buttonElement;
  const calendarDivRef = AbHooks.useComposeRef(props, partialRef, "calendarElement", isRefReady);
  const currentButtonRef = AbHooks.useComposeRef(props, partialRef, "buttonElement", isRefReady);
  partialRef.currentMonth = focusDay;
  const earliestDay = AbCalendarDay.fromData(props.earliestDay ?? DEFAULT_INPUT_EARLIEST_ALLOWED_DATE);
  const latestDay = AbCalendarDay.fromData(props.latestDay ?? DEFAULT_INPUT_LATEST_ALLOWED_DATE);
  const calcEarliestMonth = () => {
    if (earliestDay.year < focusDay.year) return undefined;
    if (earliestDay.year > focusDay.year) return 13;
    return earliestDay.month;
  };
  const calcLatestMonth = () => {
    if (latestDay.year > focusDay.year) return undefined;
    if (latestDay.year < focusDay.year) return 0;
    return latestDay.month;
  };
  const setFocusDayAndFocus = newDay => {
    setFocusDay(newDay);
    window.requestAnimationFrame(() => {
      partialRef.buttonElement?.focus();
    });
  };
  const updateFocusDay = delta => {
    const newDay = offsetDay(focusDay, delta);
    if (!newDay.inRange(props.earliestDay ?? newDay, props.latestDay ?? newDay)) return;
    setFocusDayAndFocus(newDay);
  };
  const changeMonth = delta => {
    const newFirstDay = add({
      year: focusDay.year,
      month: focusDay.month,
      day: 1
    }, {
      month: delta
    });
    const newLastDay = add(newFirstDay, {
      month: 1,
      day: -1
    });
    if (newFirstDay.compare(latestDay) > 0 || newLastDay.compare(earliestDay) < 0) return; // month out of range
    const newMonth = newFirstDay.month;
    const newMonthLength = lastDateOfMonth(newFirstDay.year, newMonth).day;
    const newDay = NumberUtils.clamp(focusDay.day, 1, newMonthLength);
    const newDate = {
      year: newFirstDay.year,
      month: newMonth,
      day: newDay
    };
    const clampedNewDate = clampDate(newDate, earliestDay, latestDay);
    if (clampedNewDate.year !== focusDay.year) {
      props.onYearChange?.(clampedNewDate.year);
    }
    setFocusDayAndFocus(clampedNewDate);
  };
  const setYear = newYear => {
    const newDay = NumberUtils.clamp(focusDay.day, 1, lastDateOfMonth(newYear, focusDay.month).day);
    const clampedNewDate = clampDate({
      year: newYear,
      month: focusDay.month,
      day: newDay
    }, earliestDay, latestDay);
    props.onYearChange?.(clampedNewDate.year);
    setFocusDayAndFocus(clampedNewDate);
  };
  const onCalendarKeyDown = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ARROW_RIGHT, () => updateFocusDay({
    day: 1
  })], [KeyValue.ARROW_LEFT, () => updateFocusDay({
    day: -1
  })], [KeyValue.ARROW_DOWN, () => updateFocusDay({
    day: 7
  })], [KeyValue.ARROW_UP, () => updateFocusDay({
    day: -7
  })]]);
  const onCalendarWheel = e => {
    // scrolling on the calendar should change months
    if (e.deltaY < 0) changeMonth(-1);
    if (e.deltaY > 0) changeMonth(1);
  };
  const prevMonthOutOfRange = !add({
    year: focusDay.year,
    month: focusDay.month,
    day: 1
  }, {
    day: -1
  }).inRange(earliestDay, latestDay);
  const nextMonthOutOfRange = !add({
    year: focusDay.year,
    month: focusDay.month,
    day: 1
  }, {
    month: 1
  }).inRange(earliestDay, latestDay);
  const renderCalendarDates = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, dateRange(previousMonthFirstVisibleDate(focusDay.year, focusDay.month), nextMonthLastVisibleDate(focusDay.year, focusDay.month)).map(day => {
      const isCurrent = focusDay.equals(day);
      const enrichment = props.dayEnrichFunction?.(day);
      const inRange = day.inRange(earliestDay, latestDay);
      const className = ReactUtils.classNamesString([AbCalendarDay.fromData({
        year: focusDay.year,
        month: focusDay.month,
        day: 1
      }).compare(day) > 0 && "ab-date-input-date-prev", add({
        year: focusDay.year,
        month: focusDay.month,
        day: 1
      }, {
        month: 1
      }).compare(day) <= 0 && "ab-date-input-date-next", day.year === focusDay.year && day.month === focusDay.month && "ab-date-input-date", enrichment?.className, isCurrent && "ab-date-input-focus-day", day.equals(props.value) && "ab-date-input-chosen-date"]);
      return /*#__PURE__*/React.createElement("div", {
        className: className,
        "data-test-id": `ab-calendar__cell-${day.year}-${day.month}-${day.day}`,
        key: day.serialize()
      }, /*#__PURE__*/React.createElement(AbButton, {
        label: day.day.toString(),
        className: "ab-no-border-button ab-square-button",
        abRef: isCurrent ? currentButtonRef : undefined,
        tooltip: inRange ? enrichment?.tooltip : AbReactResources.datePicker_invalidDate_range(DateTimeUtils.DATE_SHORT.format(earliestDay.localMidnightDate), DateTimeUtils.DATE_SHORT.format(latestDay.localMidnightDate)),
        onClick: () => {
          props.onChange?.(day);
          setFocusDay(day);
        },
        disabled: !inRange,
        tabIndex: isCurrent ? 0 : -1
      }));
    }).toArray());
  };
  return /*#__PURE__*/React.createElement("div", ReactUtils.componentProps("ab-calendar", props), /*#__PURE__*/React.createElement("div", {
    className: "ab-date-input-calendar-top-bar"
  }, /*#__PURE__*/React.createElement(AbButton, {
    icon: "absdk:chevron-left",
    className: "ab-no-border-button ab-date-input-left-chevron",
    onClick: () => changeMonth(-1),
    tooltip: prevMonthOutOfRange ? AbReactResources.datePicker_monthButton_range() : undefined,
    disabled: prevMonthOutOfRange
  }), /*#__PURE__*/React.createElement(AbMonthInput, {
    className: "ab-date-input-dropdown ab-date-input-month-dropdown",
    value: CalendarUtils.monthName(focusDay.localMidnightDate.getMonth()),
    earliestMonth: calcEarliestMonth(),
    latestMonth: calcLatestMonth(),
    onChange: value => {
      const newMonth = CalendarUtils.months.indexOf(value) + 1;
      changeMonth(newMonth - focusDay.month);
    }
  }), /*#__PURE__*/React.createElement(AbYearInput, {
    className: "ab-date-input-dropdown ab-date-input-year-dropdown",
    value: focusDay.year,
    onChange: value => setYear(value),
    maxYear: latestDay.year,
    minYear: earliestDay.year
  }), /*#__PURE__*/React.createElement(AbButton, {
    icon: "absdk:chevron-right",
    className: "ab-no-border-button ab-date-input-right-chevron",
    onClick: () => changeMonth(1),
    tooltip: nextMonthOutOfRange ? AbReactResources.datePicker_monthButton_range() : undefined,
    disabled: nextMonthOutOfRange
  })), /*#__PURE__*/React.createElement("div", {
    className: "ab-date-input-calendar",
    onWheel: onCalendarWheel,
    onKeyDown: onCalendarKeyDown,
    ref: calendarDivRef
  }, ArrayUtils.range(CalendarUtils.firstDay, CalendarUtils.firstDay + 7).map(d => {
    return /*#__PURE__*/React.createElement("p", {
      key: d,
      className: "ab-date-input-day-label"
    }, CalendarUtils.dayNameShort(d % 7));
  }), renderCalendarDates()));
};
//# sourceMappingURL=AbCalendar.js.map
